import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {BehaviorTrackerService} from './behavior-tracker.service';
import * as fromRoot from '../../../../state/app.state';
import {Observable, of} from "rxjs";
import {
  FetchResourceListFailure,
  FetchResourceListRequest,
  FetchResourceListSuccess,
  FetchSideNavWeeksListFailure,
  FetchSideNavWeeksListRequest,
  FetchSideNavWeeksListSuccess,
  FetchWeeksListFailure,
  FetchWeeksListRequest,
  FetchWeeksListSuccess,
  FetchWeekSummaryFailure,
  FetchWeekSummaryRequest,
  FetchWeekSummarySuccess,
  UpdateDailyScoreFailure,
  UpdateDailyScoreRequest,
  UpdateDailyScoreSuccess
} from "./behavior-tracker.actions";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {
  FetchSideNavWeeksListModel
} from "../models/fetch-side-nav-weeks-list.model";
import {SideNavWeekList} from "../../../../models/side-nav-week-list";
import {FetchWeeksListModel} from "../models/fetch-weeks-list.model";
import {FetchResourceListModel} from "../models/fetch-resource-list.model";
import {FetchWeekSummaryModel} from "../models/fetch-week-summary.model";
import {DailyDataModel} from "../../../../models/daily-data.model";
import {UpdateDailyScoreModel} from "../models/update-daily-score.model";
import {CommonService} from "../../../shared/services/common.service";


@Injectable()
export class BehaviorTrackerEffects {

  fetchSideNavWeeksList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSideNavWeeksListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.behaviorTrackerService.fetchSideNavWeeksList(payload).pipe(
          map((response: FetchSideNavWeeksListModel['response']) => {
            const {data, message} = response;
            const sideNavWeeksList: SideNavWeekList = data;
            return FetchSideNavWeeksListSuccess({sideNavWeeksList, message});
          }),
          catchError(() => {
            return of(FetchSideNavWeeksListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSideNavWeeksListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSideNavWeeksListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchWeeksList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchWeeksListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.behaviorTrackerService.fetchWeeksList(payload).pipe(
          map((response: FetchWeeksListModel['response']) => {
            const {data, message} = response;
            const groupInfo = data?.groupDetails;
            const weeksListInfo = data.weekInfo;
            return FetchWeeksListSuccess({weeksListInfo, groupInfo, message});
          }),
          catchError(() => {
            return of(FetchWeeksListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchWeeksListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchWeeksListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchResourceList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchResourceListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.behaviorTrackerService.fetchResourceList(payload).pipe(
          map((response: FetchResourceListModel['response']) => {
            const {data, message} = response;
            return FetchResourceListSuccess({resourceList: data, message});
          }),
          catchError(() => {
            return of(FetchResourceListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchResourceListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchResourceListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchWeekSummary$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchWeekSummaryRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchWeekSummaryModel['request']) =>
        this.behaviorTrackerService.fetchWeekSummary(payload).pipe(
          map((response: FetchWeekSummaryModel['response']) => {
            const {data, message} = response;
            const weekSummary: DailyDataModel[] = data;

            return FetchWeekSummarySuccess({
              weekSummary,
              message
            });
          }),
          catchError(() => {
            return of(FetchWeekSummaryFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchWeekSummarySuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchWeekSummaryFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  updateDailyScore$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateDailyScoreRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: UpdateDailyScoreModel['request']) =>
        this.behaviorTrackerService.updateDailyScore(payload).pipe(
          map((response: UpdateDailyScoreModel['response']) => {
            const {data, message} = response;
            const weekSummary: DailyDataModel[] = data;
            return UpdateDailyScoreSuccess({
              weekSummary,
              message
            });
          }),
          catchError(() => {
            return of(UpdateDailyScoreFailure());
          }),
          tap((action: any) => {
            if (action.type === UpdateDailyScoreSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification(action.message, 'success');
            } else if (action.type === UpdateDailyScoreFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private behaviorTrackerService: BehaviorTrackerService,
    private actions: Actions,
    private commonService: CommonService,
  ) {
  }
}
